import { useEffect, useRef, useState } from 'react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { getChatErrorMessage } from '../chat-errors';
import { getDocumentContent } from '../document-artifact';
import { exportChatDocument } from '../services/chat.api';

type DocxPreviewStatus = 'idle' | 'generating' | 'ready' | 'error';

type DocxPreviewState = {
  blob: Blob | null;
  blobUrl: string | null;
  downloadFilename: string | null;
  downloadUrl: string | null;
  error: string | null;
  status: DocxPreviewStatus;
};

type UseChatDocumentDocxPreviewArgs = {
  conversationId?: string;
  document: ChatDocumentDTO | null;
  enabled: boolean;
};

const blobCache = new Map<string, { blob: Blob; downloadFilename: string; downloadUrl: string }>();
const MAX_CACHE_ENTRIES = 12;

function djb2(value: string) {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

function buildCacheKey(documentId: string, content: string) {
  return `${documentId}:${djb2(content)}`;
}

function rememberInCache(key: string, entry: { blob: Blob; downloadFilename: string; downloadUrl: string }) {
  blobCache.set(key, entry);

  if (blobCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = blobCache.keys().next().value;
    if (oldestKey) {
      blobCache.delete(oldestKey);
    }
  }
}

const INITIAL_STATE: DocxPreviewState = {
  blob: null,
  blobUrl: null,
  downloadFilename: null,
  downloadUrl: null,
  error: null,
  status: 'idle',
};

export function useChatDocumentDocxPreview({
  conversationId,
  document,
  enabled,
}: UseChatDocumentDocxPreviewArgs) {
  const [state, setState] = useState<DocxPreviewState>(INITIAL_STATE);
  const objectUrlRef = useRef<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const documentId = document?.documentId ?? null;
  const documentStatus = document?.status ?? null;
  const documentContent = document ? getDocumentContent(document).trim() : '';
  const hasDirectSourceUrl = Boolean(document?.sourceUrl);

  useEffect(() => {
    if (!enabled || !conversationId || !documentId || hasDirectSourceUrl) {
      return;
    }

    if (documentStatus !== 'ready' || documentContent.length === 0) {
      return;
    }

    const cacheKey = buildCacheKey(documentId, documentContent);
    const cached = blobCache.get(cacheKey);

    if (cached) {
      const blobUrl = URL.createObjectURL(cached.blob);
      objectUrlRef.current = blobUrl;
      setState({
        blob: cached.blob,
        blobUrl,
        downloadFilename: cached.downloadFilename,
        downloadUrl: cached.downloadUrl,
        error: null,
        status: 'ready',
      });

      return () => {
        URL.revokeObjectURL(blobUrl);
        if (objectUrlRef.current === blobUrl) {
          objectUrlRef.current = null;
        }
      };
    }

    let cancelled = false;
    setState((previous) => ({ ...previous, error: null, status: 'generating' }));

    (async () => {
      try {
        const exported = await exportChatDocument(conversationId, documentId, 'docx');
        const response = await fetch(exported.downloadUrl, { credentials: 'omit' });

        if (!response.ok) {
          throw new Error(`docx_preview_fetch_failed_${response.status}`);
        }

        const blob = await response.blob();

        if (cancelled) {
          return;
        }

        rememberInCache(cacheKey, {
          blob,
          downloadFilename: exported.filename,
          downloadUrl: exported.downloadUrl,
        });

        const blobUrl = URL.createObjectURL(blob);
        objectUrlRef.current = blobUrl;
        setState({
          blob,
          blobUrl,
          downloadFilename: exported.filename,
          downloadUrl: exported.downloadUrl,
          error: null,
          status: 'ready',
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        setState({
          blob: null,
          blobUrl: null,
          downloadFilename: null,
          downloadUrl: null,
          error: getChatErrorMessage(error),
          status: 'error',
        });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [conversationId, documentId, documentStatus, documentContent, enabled, hasDirectSourceUrl, retryToken]);

  function retry() {
    if (documentId && documentContent.length > 0) {
      blobCache.delete(buildCacheKey(documentId, documentContent));
    }

    setState(INITIAL_STATE);
    setRetryToken((token) => token + 1);
  }

  return {
    blob: state.blob,
    blobUrl: state.blobUrl,
    downloadFilename: state.downloadFilename,
    downloadUrl: state.downloadUrl,
    error: state.error,
    retry,
    status: state.status,
  };
}
